import React, { useMemo } from 'react';
import { computeMatchContext, renderHighlightedValue } from '../../../shared/utils/matchContext';
import './LifecycleToolList.css';

/* ── Human-readable labels for completeness fields ─────────────────── */
const FIELD_LABELS = {
  warranty_shots: 'Warranty Pieces',
  actual_shots_to_date: 'Actual Pieces',
  weekly_capacity: 'Weekly Capacity',
  current_condition_score: 'Condition Score',
  warranty_shots_source: 'Warranty Source',
  cavity_number_start: 'Cavity Start',
  cavity_number_end: 'Cavity End',
  lifecycle_detail: 'Location Detail',
  assessment_notes: 'Assessment Notes',
  scr_status: 'SCR Status',
  ownership: 'Ownership',
  alv_plant: 'ALV Plant',
  service_type: 'Service Type',
  part_numbers: 'Part Numbers',
};

function humanLabel(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  // Fallback: title-case with underscores → spaces
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/* ── Completeness check ──────────────────────────────────────────────── */
function getCompleteness(tool) {
  // Use backend completeness if available
  if (tool.completeness) {
    return {
      complete: tool.completeness.is_complete,
      missing: (tool.completeness.missing_fields || []).map(humanLabel),
    };
  }
  // Fallback client-side check
  const missing = [];
  if (tool.warranty_shots == null) missing.push('Warranty Pieces');
  if (tool.weekly_capacity == null) missing.push('Weekly Capacity');
  if (tool.actual_shots_to_date == null) missing.push('Actual Pieces');
  if (tool.current_condition_score == null) missing.push('Condition Score');
  if ((tool.pn_count || 0) < 1) missing.push('Part Numbers');
  return { complete: missing.length === 0, missing };
}

/* ── Disposition badge config ────────────────────────────────────────── */
const DISP_CONFIG = {
  URGENT:               { label: 'Urgent',          cls: 'ltl-disp-urgent' },
  NEAR_TERM:            { label: 'Near-term',        cls: 'ltl-disp-near' },
  MONITORING:           { label: 'Monitoring',       cls: 'ltl-disp-monitor' },
  CONDITION_UNASSESSED: { label: 'Condition Needed', cls: 'ltl-disp-condition-needed' },
  SHOTS_INCOMPLETE:     { label: 'Pieces Pending',   cls: 'ltl-disp-shots-pending' },
};

/* ── Sort: disposition first (urgency), then list_number, then completeness */
const DISP_ORDER = { URGENT: 0, NEAR_TERM: 1, MONITORING: 2 };

function sortTools(tools) {
  return [...tools].sort((a, b) => {
    // 1. disposition_category: URGENT first, null/not-calculated last
    const dcA = DISP_ORDER[a.disposition_category] ?? 3;
    const dcB = DISP_ORDER[b.disposition_category] ?? 3;
    if (dcA !== dcB) return dcA - dcB;

    // 2. list_number ascending, nulls/empty at bottom
    const lnA = a.list_number || '';
    const lnB = b.list_number || '';
    const lnEmptyA = lnA === '' ? 1 : 0;
    const lnEmptyB = lnB === '' ? 1 : 0;
    if (lnEmptyA !== lnEmptyB) return lnEmptyA - lnEmptyB;
    if (lnA !== lnB) return lnA < lnB ? -1 : 1;

    // 3. incomplete before complete
    const compA = getCompleteness(a).complete ? 1 : 0;
    const compB = getCompleteness(b).complete ? 1 : 0;
    return compA - compB;
  });
}

export default function LifecycleToolList({ tools, selectedToolId, onSelectTool, loading, searchQuery }) {
  const sorted = useMemo(() => sortTools(tools || []), [tools]);

  if (loading) {
    return <div className="ltl-loading">Loading tools...</div>;
  }

  if (sorted.length === 0) {
    return <div className="ltl-empty">No tools found for this site.</div>;
  }

  return (
    <div className="ltl-list">
      {sorted.map((tool, index) => {
        const id = tool.id || tool.tool_id;
        const { complete, missing } = getCompleteness(tool);
        const dispKey = tool.disposition_category
          || (tool.eow_skip_reason === 'CONDITION_UNASSESSED' ? 'CONDITION_UNASSESSED' : 'SHOTS_INCOMPLETE');
        const disp = DISP_CONFIG[dispKey];
        const listNum = tool.list_number;
        const isActive = selectedToolId === id;
        const matchCtx = computeMatchContext(tool, searchQuery);

        return (
          <button
            key={id}
            className={`ltl-row${isActive ? ' active' : ''}`}
            onClick={() => onSelectTool(id)}
          >
            {/* Top line: row number + ID + badges */}
            <div className="ltl-row-top">
              <span className="ltl-row-num">{index + 1}</span>
              <span className="ltl-tool-id">
                {tool.supplier_tool_id || tool.tool_id || id}
              </span>
              <div className="ltl-badges">
                {listNum && (
                  <span className="ltl-badge ltl-badge-list">
                    <span className="ltl-badge-label">List:&nbsp;</span> {listNum}
                  </span>
                )}
                {disp && (
                  <span className={`ltl-badge ${disp.cls}`}>
                    <span className="ltl-badge-label">EOW:&nbsp;</span> {disp.label}
                  </span>
                )}
                {tool.recon_status && (
                  <span className="ltl-badge ltl-badge-status">
                    <span className="ltl-badge-label">Status:&nbsp;</span> {tool.recon_status === 'OPEN' ? 'Open' : tool.recon_status === 'CLOSED' ? 'Closed' : tool.recon_status}
                  </span>
                )}
              </div>
            </div>

            {/* Alt ID */}
            {tool.alt_tool_id && (
              <div className="ltl-alt-id">{tool.alt_tool_id}</div>
            )}

            {/* Supplier name */}
            {tool.supplier_name && (
              <div className="ltl-supplier">{tool.supplier_name}</div>
            )}

            {/* Completeness */}
            <div className="ltl-completeness">
              {complete ? (
                <span className="ltl-complete-ok">&#10003; Complete</span>
              ) : (
                <span className="ltl-complete-missing">
                  Missing: {missing.join(', ')}
                </span>
              )}
            </div>

            {/* Match context badge — shows which field matched the search */}
            {matchCtx && (
              <span className="ltl-badge ltl-badge-list" style={{ marginTop: 'var(--space-1)' }}>
                <span className="ltl-badge-label">Matched:&nbsp;</span>
                {matchCtx.label} &middot; {renderHighlightedValue(matchCtx.value, searchQuery)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
